import { motion } from "framer-motion";
import { useTheme } from "@/contexts/ThemeContext";
import { Users, Target, Award, Lightbulb } from "lucide-react";

export const AboutUs = () => {
  const { colors } = useTheme();

  const features = [
    {
      icon: Users,
      title: "Expert Team",
      description: "Our diverse team brings years of experience in entertainment and digital marketing."
    },
    {
      icon: Target,
      title: "Strategic Focus",
      description: "We develop targeted strategies that align with your brand's vision and goals."
    },
    {
      icon: Award,
      title: "Proven Results",
      description: "Track record of successful campaigns and satisfied clients across various industries."
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "Cutting-edge approaches that keep your brand ahead in the digital landscape."
    }
  ];

  return (
    <section 
      id="about-us" 
      className="py-20 transition-colors duration-300"
      style={{ backgroundColor: colors.background }}
    >
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 
            className="text-4xl md:text-5xl font-bold mb-6"
            style={{
              background: colors.gradient,
              backgroundSize: "200% auto",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            About Us
          </h2>
          <motion.div 
            className="w-24 h-1 mx-auto mb-8"
            style={{ backgroundColor: colors.primary }}
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          />
          <p 
            className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed"
            style={{ color: colors.secondary }}
          >
            Agape Entertainment is a full-service creative agency built for storytellers. With expertise across media production, marketing, anti-piracy, and celebrity representation, we deliver seamless experiences from pitch to premiere. <br /><br />
            We blend creativity, strategy, and digital precision to unlock the full potential of every project. Our commitment is to authenticity, innovation, and the timeless love of cinema.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="text-center group"
            >
              <motion.div
                className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${colors.primary}20` }}
                whileHover={{ 
                  scale: 1.1,
                  backgroundColor: `${colors.primary}30`
                }}
                animate={{
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  scale: {
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }
                }}
              >
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: index * 0.2
                  }}
                >
                  <feature.icon size={32} style={{ color: colors.primary }} />
                </motion.div>
              </motion.div>
              <h3 
                className="text-xl font-semibold mb-2"
                style={{ color: colors.foreground }}
              >
                {feature.title}
              </h3>
              <p 
                className="text-sm leading-relaxed"
                style={{ color: colors.secondary }}
              >
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Updated "Our Vision" Card to match Our Mission card style */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 relative"
          style={{ padding: '20px 0' }}
        >
          {/* Greatly reduced glow effects - matching Our Mission */}
          <motion.div 
            className="absolute inset-0 rounded-2xl opacity-12 group-hover:opacity-20 transition-all duration-700"
            style={{
              background: `radial-gradient(circle at center, ${colors.primary}05, ${colors.secondary}03, transparent 70%)`,
              filter: 'blur(35px)',
              transform: 'scale(1.2)',
              boxShadow: `0 0 30px ${colors.primary}05, 0 0 50px ${colors.primary}02`
            }}
          />
          
          <motion.div 
            className="absolute inset-0 rounded-2xl opacity-15 group-hover:opacity-25 transition-all duration-500"
            style={{
              background: `linear-gradient(135deg, ${colors.primary}04, ${colors.secondary}03)`,
              filter: 'blur(20px)',
              transform: 'scale(1.08)'
            }}
          />
          
          <motion.div
            whileHover={{ 
              scale: 1.01, // Much reduced hover effect
              y: -2,
              transition: { duration: 0.5, type: "spring", bounce: 0.1 }
            }}
            className="relative backdrop-blur-xl border-2 rounded-2xl shadow-2xl group"
            style={{ 
              backgroundColor: `${colors.card}98`,
              borderColor: `${colors.primary}65`, // Reduced border intensity
              boxShadow: `
                0 15px 30px ${colors.primary}08, 
                0 8px 20px ${colors.primary}05, 
                0 4px 12px ${colors.primary}03,
                inset 0 1px 2px ${colors.primary}12,
                inset 0 -1px 2px ${colors.secondary}06
              `,
              backdropFilter: 'blur(15px) saturate(1.5)',
            }}
          >
            <div className="p-10 md:p-14">
              {/* Much reduced inner effects - matching Our Mission */}
              <div 
                className="absolute inset-0 rounded-2xl opacity-8 group-hover:opacity-15 transition-opacity duration-400"
                style={{
                  background: `radial-gradient(circle at 30% 30%, ${colors.primary}08 0%, ${colors.secondary}04 30%, transparent 70%)`
                }}
              />
              
              <div 
                className="absolute inset-2 rounded-xl opacity-12 group-hover:opacity-20 transition-opacity duration-300"
                style={{
                  background: `linear-gradient(135deg, ${colors.background}08, ${colors.card}04, ${colors.primary}02)`,
                  backdropFilter: 'blur(8px)',
                }}
              />
              
              <motion.div
                className="relative z-10 max-w-4xl mx-auto text-center"
                animate={{
                  backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"]
                }}
                transition={{
                  duration: 12, // Slower animation
                  repeat: Infinity,
                  ease: "linear"
                }}
              >
                <h3 
                  className="text-2xl md:text-3xl font-bold mb-6"
                  style={{ 
                    color: colors.primary,
                    textShadow: `0 2px 4px ${colors.primary}15` // Reduced shadow
                  }}
                >
                  Our Vision
                </h3>
                <p 
                  className="text-lg md:text-xl max-w-4xl leading-relaxed"
                  style={{ 
                    color: colors.secondary,
                    textShadow: `0 1px 2px ${colors.foreground}05`
                  }}
                >
                  Our vision is to build an entertainment ecosystem where every story finds its voice, every creator finds their power, and every idea becomes an experience. We aspire to lead with purpose-crafting content that connects, campaigns that elevate, and strategies that protect, all while staying ahead of what's next.

                </p>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};